import React, { useEffect, useState, useMemo } from 'react';
import { Card, Col, Row, Space, Typography, Collapse, Statistic, Divider, Table, Tag } from 'antd';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { DollarOutlined, CalendarOutlined, AppstoreOutlined } from '@ant-design/icons';
import { formatCurrency2SigFig } from '../../../core/utils/campaigns';
import { usePermify } from '@permify/react-role';

import CampaignRequestorReviewDetails from './CampaignRequestorReviewDetails';
import CampaignRequestorReviewBudget from './CampaignRequestorReviewBudget';
import CampaignRequestorCategory from './CampaignRequestorReviewCategory';
import CampaignRequestorReviewAttribution from './CampaignRequestorReviewAttribution';
import CampaignRequestorReviewLineItem from './CampaignRequestorReviewLineItem';
import CampaignRequestorReviewSummary from './CampaignRequestorReviewSummary';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { isPercentageModel } from '../../../core/utils/pricing/cpmHelper';
import OverlaySpinner from '../../shared/OverlaySpinner';

const { Text, Title } = Typography;
const { Panel } = Collapse;

// Standalone review component that fetches its own data
const CampaignRequestorReview = ({ editable = false }) => {
  const { id } = useParams();
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [showCampaignFlightSchedules, setShowCampaignFlightSchedules] = useState(false);
  const { isAuthorized } = usePermify();

  const {
    fetchCampaignOrder,
    currentCampaignOrder,
    loadingCampaignOrder,
    selectedAdvertiser,
  } = useCampaignRequestorContext();

  // Check if pricing model is Percentage (hide CPM fields)
  // For existing campaigns, use the saved pricingModel; for new campaigns, use advertiser's pricing model
  const savedPricingModel = currentCampaignOrder?.pricingModel;
  const hideCpmFields = useMemo(() => {
    if (savedPricingModel) {
      return savedPricingModel === 'Percentage';
    }
    return isPercentageModel(selectedAdvertiser);
  }, [savedPricingModel, selectedAdvertiser]);

  // Fetch campaign data when component mounts
  useEffect(() => {
    if (id && fetchCampaignOrder) {
      fetchCampaignOrder({
        variables: {
          where: { id },
        },
      });
    }
  }, [id, fetchCampaignOrder]);

  // Process campaign data when it's loaded
  useEffect(() => {
    if (currentCampaignOrder && !loadingCampaignOrder) {

      // Process campaigns with nested structure
      const processedCampaigns = (currentCampaignOrder.campaigns || []).map((campaign, index) => ({
        id: campaign.id,
        name: campaign.name || `Campaign ${index + 1}`,
        type: campaign.type || currentCampaignOrder.type || 1,
        budget: campaign.budgetTotal || campaign.budget || 0,
        startDate: campaign.startDate ? moment(campaign.startDate) : null,
        endDate: campaign.endDate ? moment(campaign.endDate) : null,
        lineItems: (campaign.lineItems || []).map(lineItem => ({
          ...lineItem,
          startDate: lineItem.startDate ? moment(lineItem.startDate) : null,
          endDate: lineItem.endDate ? moment(lineItem.endDate) : null,
        })),
        flightSchedules: campaign.flightSchedules || [],
      }));

      setReviewData({
        name: currentCampaignOrder.name || '',
        description: currentCampaignOrder.description || '',
        advertiser: currentCampaignOrder.advertiser,
        startDate: currentCampaignOrder.startDate ? moment(currentCampaignOrder.startDate) : null,
        endDate: currentCampaignOrder.endDate ? moment(currentCampaignOrder.endDate) : null,
        budget: currentCampaignOrder.budget || 0,
        cpm: currentCampaignOrder.cpm || 7,
        category: currentCampaignOrder.category,
        advertiserType: currentCampaignOrder.advertiserType,
        isRegulated: currentCampaignOrder.isRegulated,
        type: currentCampaignOrder.type || 1,
        renewalStatus: currentCampaignOrder.renewalStatus,
        campaigns: processedCampaigns,
        attributionTargets: currentCampaignOrder.attributionTargets || [],
        geoTargets: currentCampaignOrder.geoTargets || [],
        notes: currentCampaignOrder.notes || '',
        IONumber: currentCampaignOrder.IONumber || '',
        additionalIONumbers: currentCampaignOrder.additionalIONumbers || [],
        productCode: currentCampaignOrder.productCode || 'DIS',
        awarenessCampaign: currentCampaignOrder.awarenessCampaign || false,
        transactionTarget: currentCampaignOrder.transactionTarget,
        transactionsConfig: currentCampaignOrder.transactionsConfig,
        signUpConfig: currentCampaignOrder.signUpConfig,
        sessionConfig: currentCampaignOrder.sessionConfig,
        measureWalkInTraffic: currentCampaignOrder.measureWalkInTraffic || false,
        complianceAudiences: currentCampaignOrder.complianceAudiences || [],
      });
      setLoading(false);
    }
  }, [currentCampaignOrder, loadingCampaignOrder]);

  // Advanced Mode Detection Logic
  useEffect(() => {
    const checkAdvancedMode = async () => {
      // Check if user has required permissions (isTrafficker OR isProcessor)
      const hasPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);

      // Check if campaign status is NOT DRAFT
      const campaignStatus = currentCampaignOrder?.status;
      const isNotDraft = campaignStatus && campaignStatus !== 'DRAFT';

      // Check if at least one campaign has hasMediaPlanStrategy enabled
      const campaigns = currentCampaignOrder?.campaigns || [];
      const hasStrategy = campaigns.some(campaign => campaign.hasMediaPlanStrategy === true);

      // Advanced Mode is available when ALL conditions are met
      const canAccessAdvancedMode = hasPermission && isNotDraft && hasStrategy;

      setIsAdvancedMode(canAccessAdvancedMode);

      // Show campaign flight schedules: non-DRAFT status + Ad Ops permission
      setShowCampaignFlightSchedules(hasPermission && isNotDraft);
    };

    checkAdvancedMode();
  }, [isAuthorized, currentCampaignOrder]);

  if (loading || loadingCampaignOrder || !reviewData) {
    return <OverlaySpinner />;
  }

  const campaigns = reviewData.campaigns || [];

  // Campaign type labels
  const campaignTypeLabels = {
    1: 'Display',
    7: 'External',
    8: 'Video',
  };

  return (
    <div style={{ margin: 20 }}>
      <Row gutter={20}>
        <Col span={6}>
          <CampaignRequestorReviewSummary
            formValues={reviewData}
            currentCampaignOrder={currentCampaignOrder}
            editable={editable}
          />
        </Col>
        <Col span={18}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div id="requester-details">
              <CampaignRequestorReviewDetails formValues={reviewData} />
            </div>

            <div id="requester-budget-allocation">
              <CampaignRequestorReviewBudget
                formValues={reviewData}
                onBudgetChange={(updatedCampaigns) => {
                  // Update campaigns in reviewData if needed for real-time updates
                  setReviewData(prev => ({
                    ...prev,
                    campaigns: updatedCampaigns
                  }));
                }}
                isAdvancedMode={isAdvancedMode}
                campaignOrderStatus={currentCampaignOrder?.status}
              />
            </div>

            <div id="requester-categories">
              <CampaignRequestorCategory formValues={reviewData} />
            </div>

            <div id="requester-attribution-reporting">
              <CampaignRequestorReviewAttribution formValues={reviewData} />
            </div>

            {/* Multi-Campaign Grouped View */}
            <Card title={<Title level={5}>Campaigns & Line Items</Title>}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Collapse
                  defaultActiveKey={campaigns.map((_, index) => index.toString())}
                  style={{ backgroundColor: '#fafafa' }}
                >
                  {campaigns.map((campaign, campaignIndex) => {
                    const lineItems = campaign.lineItems || [];
                    const totalLineItemBudget = lineItems.reduce(
                      (sum, item) => sum + (parseFloat(item.budgetTotal) || 0),
                      0
                    );

                    return (
                      <Panel
                        key={campaignIndex.toString()}
                        header={
                          <Row justify="space-between" align="middle">
                            <Col>
                              <Space size="middle">
                                <Text strong style={{ fontSize: 16 }}>
                                  {campaign.name || `Campaign ${campaignIndex + 1}`}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 14 }}>
                                  ({campaignTypeLabels[reviewData.type] || 'Display'})
                                </Text>
                              </Space>
                            </Col>
                            <Col>
                              <Space size="large">
                                <Statistic
                                  title="Budget"
                                  value={campaign.budget || 0}
                                  precision={2}
                                  prefix={<DollarOutlined />}
                                  valueStyle={{ fontSize: 16 }}
                                />
                                <Statistic
                                  title="Line Items"
                                  value={lineItems.length}
                                  prefix={<AppstoreOutlined />}
                                  valueStyle={{ fontSize: 16 }}
                                />
                                {campaign.startDate && campaign.endDate && (
                                  <div style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                      Date Range
                                    </Text>
                                    <Space size="small">
                                      <CalendarOutlined />
                                      <Text style={{ fontSize: 14 }}>
                                        {campaign.startDate.format('MM/DD/YY')} - {campaign.endDate.format('MM/DD/YY')}
                                      </Text>
                                    </Space>
                                  </div>
                                )}
                              </Space>
                            </Col>
                          </Row>
                        }
                        style={{ marginBottom: 12 }}
                      >
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          {/* Campaign Summary */}
                          <Row gutter={16} style={{ padding: '12px', backgroundColor: '#fff', borderRadius: '4px' }}>
                            <Col span={8}>
                              <Text type="secondary">Campaign Budget:</Text>{' '}
                              <Text strong>${campaign.budget?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                            </Col>
                            <Col span={8}>
                              <Text type="secondary">Line Item Total:</Text>{' '}
                              <Text strong>${totalLineItemBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                            </Col>
                            <Col span={8}>
                              <Text type="secondary">Utilization:</Text>{' '}
                              <Text strong>
                                {campaign.budget > 0 ? Math.round((totalLineItemBudget / campaign.budget) * 100) : 0}%
                              </Text>
                            </Col>
                          </Row>

                          {/* Campaign-Level Flight Schedules */}
                          {showCampaignFlightSchedules && campaign.flightSchedules && campaign.flightSchedules.length > 0 && (
                            <Row gutter={16} style={{ padding: '12px', backgroundColor: '#fff', borderRadius: '4px', marginTop: 8 }}>
                              <Col span={24}>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    CAMPAIGN FLIGHT SCHEDULES
                                  </Text>
                                  <Table
                                    dataSource={campaign.flightSchedules.map((flight, idx) => {
                                      const numberOfDays = flight.startDate && flight.endDate
                                        ? moment.utc(flight.endDate).diff(moment.utc(flight.startDate), 'days') + 1
                                        : null;
                                      const impressionGoal = flight.budget && flight.cpm
                                        ? Math.round((flight.budget / flight.cpm) * 1000)
                                        : null;
                                      return {
                                        ...flight,
                                        key: flight.id || idx,
                                        numberOfDays,
                                        impressionGoal,
                                      };
                                    })}
                                    columns={[
                                      {
                                        title: 'Flight',
                                        key: 'flight',
                                        width: 80,
                                        render: (_, __, idx) => (
                                          <Tag color="blue">Flight {idx + 1}</Tag>
                                        ),
                                      },
                                      {
                                        title: 'Start Date',
                                        dataIndex: 'startDate',
                                        key: 'startDate',
                                        render: (date) => date ? moment.utc(date).format('MM/DD/YYYY') : '-',
                                      },
                                      {
                                        title: 'End Date',
                                        dataIndex: 'endDate',
                                        key: 'endDate',
                                        render: (date) => date ? moment.utc(date).format('MM/DD/YYYY') : '-',
                                      },
                                      {
                                        title: 'Budget',
                                        dataIndex: 'budget',
                                        key: 'budget',
                                        render: (budget) => budget ? formatCurrency2SigFig(budget) : '-',
                                      },
                                      // CPM column - hidden for Percentage pricing
                                      ...(!hideCpmFields ? [{
                                        title: 'CPM',
                                        dataIndex: 'cpm',
                                        key: 'cpm',
                                        render: (cpm) => cpm ? `$${cpm}` : '-',
                                      }] : []),
                                      // Impressions column - hidden for Percentage pricing
                                      ...(!hideCpmFields ? [{
                                        title: 'Impressions',
                                        dataIndex: 'impressionGoal',
                                        key: 'impressionGoal',
                                        render: (impressions) => impressions ? impressions.toLocaleString() : '-',
                                      }] : []),
                                      {
                                        title: 'Days',
                                        dataIndex: 'numberOfDays',
                                        key: 'numberOfDays',
                                        width: 70,
                                        align: 'center',
                                        render: (days) => days !== null ? days : '-',
                                      },
                                    ]}
                                    pagination={false}
                                    size="small"
                                    style={{ marginTop: 8 }}
                                  />
                                  <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                    Total: {campaign.flightSchedules.length} flight schedule(s)
                                  </div>
                                </Space>
                              </Col>
                            </Row>
                          )}

                          <Divider style={{ margin: '8px 0' }} />

                          {/* Line Items within this campaign */}
                          {lineItems.map((lineItem, lineItemIndex) => (
                            <div key={lineItemIndex} id={`requester-campaign-${campaignIndex}-line-item-${lineItemIndex}`}>
                              <CampaignRequestorReviewLineItem
                                formValues={{
                                  ...reviewData,
                                  // Override with campaign-specific dates if available
                                  startDate: campaign.startDate || reviewData.startDate,
                                  endDate: campaign.endDate || reviewData.endDate,
                                }}
                                campaign={lineItem}
                                index={lineItemIndex}
                                campaignOrderStatus={currentCampaignOrder?.status}
                                hideCpmFields={hideCpmFields}
                              />
                            </div>
                          ))}

                          {lineItems.length === 0 && (
                            <div style={{ padding: '24px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '4px' }}>
                              <Text type="secondary">No line items in this campaign</Text>
                            </div>
                          )}
                        </Space>
                      </Panel>
                    );
                  })}
                </Collapse>

                {campaigns.length === 0 && (
                  <div style={{ padding: '24px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '4px' }}>
                    <Text type="secondary">No campaigns configured</Text>
                  </div>
                )}
              </Space>
            </Card>

            <div id="campaign-notes">
              <Card title="CAMPAIGN NOTES">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Row gutter={20}>
                    <Col span={24}>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                          Additional Details
                        </Text>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#fafafa',
                          border: '1px solid #f0f0f0',
                          borderRadius: '6px',
                          minHeight: '60px'
                        }}>
                          <Text style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                            {reviewData.notes || 'No additional notes provided'}
                          </Text>
                        </div>
                      </Space>
                    </Col>
                  </Row>
                </Space>
              </Card>
            </div>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default CampaignRequestorReview;
